/**
 * Form Field Overlay Component
 *
 * Renders PDF form fields as interactive overlays on PDF pages
 * Supports text fields, checkboxes, dropdowns, and multiline text
 *
 * UNIFIED ARCHITECTURE:
 * - Receives allFields directly from PDFJSViewer (single source of truth)
 * - Field values stored in field objects (not separate)
 * - No distinction between embedded and user-created fields
 */

import React, { useCallback } from 'react';
import { UnifiedFormField } from './utils/pdfFieldUtils';
import styles from './FormFieldOverlay.module.scss';

export interface FormFieldOverlayProps {
  pageNumber: number;
  pageWidth: number;
  pageHeight: number;
  formFields: UnifiedFormField[];
  onUpdateValue: (fieldId: string, value: any) => void;
  zoom: number;
  readOnly?: boolean;
}

export const FormFieldOverlay: React.FC<FormFieldOverlayProps> = ({
  pageNumber,
  pageWidth,
  pageHeight,
  formFields,
  onUpdateValue,
  zoom,
  readOnly = false,
}) => {
  const overlayRef = React.useRef<HTMLDivElement>(null);

  // Get fields for this page (TWO-PHASE: filter by pageNum)
  // In readonly mode, hide empty TEXT fields (checkboxes always show)
  const pageFields = formFields.filter(f => {
    if (f.pageNum !== pageNumber) return false;
    // In readonly mode:
    // - Checkboxes: ALWAYS show (checked or unchecked)
    // - Text fields: ONLY show if they have a value (hide empty placeholders)
    if (readOnly) {
      if (f.type === 'checkbox') return true; // Always show checkboxes
      // Hide text fields that are empty (null, undefined, empty string, or whitespace-only)
      if (f.type === 'textfield') {
        const hasValue = f.value != null && String(f.value).trim().length > 0;
        if (!hasValue) return false; // Hide empty text fields
      }
    }
    return true;
  });

  /**
   * Handle text field change
   */
  const handleTextChange = useCallback((fieldId: string, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (readOnly) return;
    onUpdateValue(fieldId, e.target.value);
  }, [readOnly, onUpdateValue]);

  /**
   * Handle checkbox change
   */
  const handleCheckboxChange = useCallback((fieldId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    onUpdateValue(fieldId, e.target.checked);
  }, [readOnly, onUpdateValue]);

  // Removed: handleDropdownChange - UnifiedFormField doesn't support dropdown type

  return (
    <div
      ref={overlayRef}
      className={styles.formFieldLayer}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: `${pageWidth}px`,
        height: `${pageHeight}px`,
        pointerEvents: 'none', // Allow clicks through - individual fields have pointer-events: auto
      }}
    >
      {pageFields.map((field) => {
        // Convert PDF coordinates to screen coordinates by scaling with zoom
        const screenX = field.x * zoom;
        const screenY = field.y * zoom;
        const screenW = field.width * zoom;
        const screenH = field.height * zoom;

        // Use field.value directly (single source of truth)
        const value = field.value ?? '';
        const isDisabled = readOnly || field.readOnly;
        // Legacy records stored the raw PDF fieldName (often author-tool junk
        // like `Client` / `topmostSubform[0]...`) as `placeholder`. Suppress
        // the placeholder when it matches the fieldName — we never want to
        // leak an internal identifier as visible placeholder text.
        const effectivePlaceholder =
          field.placeholder && field.placeholder !== field.fieldName
            ? field.placeholder
            : '';

        return (
          <div
            key={field.id}
            className={styles.formField}
            style={{
              left: `${screenX}px`,
              top: `${screenY}px`,
              width: `${screenW}px`,
              height: `${screenH}px`,
            }}
          >
            {(field.type === 'textfield' && !field.multiline) && (
              <input
                type="text"
                value={typeof value === 'boolean' ? '' : (value || '')}
                onChange={(e) => handleTextChange(field.id, e)}
                readOnly={isDisabled}
                className={styles.textField}
                placeholder={readOnly ? '' : effectivePlaceholder}
                style={{
                  width: '100%',
                  height: '100%',
                  fontSize: field.fontSize ? `${field.fontSize * zoom}px` : `${Math.max(8, Math.min(14, 11 * zoom))}px`,
                  color: field.fontColor || undefined,
                  textAlign: field.horizontalAlign || 'left',
                }}
              />
            )}
            {(field.type === 'textfield' && field.multiline) && (
              <textarea
                value={typeof value === 'boolean' ? '' : (value || '')}
                onChange={(e) => handleTextChange(field.id, e)}
                readOnly={isDisabled}
                className={styles.textareaField}
                placeholder={readOnly ? '' : effectivePlaceholder}
                style={{
                  width: '100%',
                  height: '100%',
                  fontSize: field.fontSize ? `${field.fontSize * zoom}px` : `${Math.max(8, Math.min(14, 11 * zoom))}px`,
                  color: field.fontColor || undefined,
                  resize: 'none',
                  textAlign: field.horizontalAlign || 'left',
                }}
              />
            )}
            {field.type === 'checkbox' && (
              <input
                type="checkbox"
                checked={
                  // Handle various truthy checkbox representations from JSONB/PDF.js
                  // PDF.js can return: true (boolean), "true"/"Yes"/"On" (strings), 1 (number)
                  value === true || value === 'true' || (value as any) === 1 ||
                  value === 'Yes' || value === 'On' || value === 'yes' || value === 'on'
                }
                onChange={(e) => !isDisabled && handleCheckboxChange(field.id, e)}
                aria-disabled={isDisabled}
                className={`${styles.checkboxField} ${isDisabled ? styles.checkboxFieldReadonly : ''}`}
                style={{
                  width: `${Math.min(screenW, screenH)}px`,
                  height: `${Math.min(screenW, screenH)}px`,
                  maxWidth: '100%',
                  maxHeight: '100%',
                  pointerEvents: isDisabled ? 'none' : 'auto',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
